import type IEquatable from "../types/IEquatable";

function isPlainObject(value: object): boolean {
	const proto = Object.getPrototypeOf(value);
	return proto === Object.prototype || proto === null;
}

/**
 * The single equality algorithm used across the codebase. Applied at every level (including array
 * elements and nested object values), in order:
 *
 * 1. Reference / `Object.is` (covers primitives, `NaN`, and identical references).
 * 2. `IEquatable.equals()` when the left operand exposes an `equals` method.
 * 3. `Date` compared by timestamp.
 * 4. Arrays compared element-wise (same length, same order).
 * 5. Plain objects compared by own enumerable keys.
 *
 * `null`/`undefined` are only equal to themselves. {@link arrayEquals} delegates here so every
 * equality check shares this behavior.
 */
export default function deepEquals(a: unknown, b: unknown): boolean {
	// `a !== a` is true only for NaN, so this matches Object.is for NaN without the function call.
	if (a === b || (a !== a && b !== b)) {
		return true;
	}

	if (a == null || b == null) {
		return false;
	}

	if (typeof (a as Partial<IEquatable<object>>).equals === "function") {
		return (a as IEquatable<object>).equals(b as object);
	}

	if (a instanceof Date || b instanceof Date) {
		//both sides must be Dates: a keyless Date would otherwise structurally equal {} (asymmetrically)
		return a instanceof Date && b instanceof Date && a.getTime() === b.getTime();
	}

	if (Array.isArray(a)) {
		if (!Array.isArray(b) || a.length !== b.length) {
			return false;
		}

		for (let i = 0; i < a.length; i++) {
			if (!deepEquals(a[i], b[i])) {
				return false;
			}
		}

		return true;
	}

	if (typeof a !== "object" || typeof b !== "object") {
		return false;
	}

	const keysA = Object.keys(a);
	const keysB = Object.keys(b);

	if (keysA.length !== keysB.length) {
		return false;
	}

	// A non-plain instance with no own enumerable keys (File, Blob, Map, Set, DOM nodes) has
	// nothing structural to compare — two different ones would otherwise be "equal", which
	// made setValue silently drop a replaced FileList. Only reference identity counts there.
	if (keysA.length === 0 && (!isPlainObject(a) || !isPlainObject(b))) {
		return false;
	}

	for (const key of keysA) {
		if (!Object.prototype.hasOwnProperty.call(b, key)) {
			return false;
		}

		if (!deepEquals((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])) {
			return false;
		}
	}

	return true;
}
